import { City } from "../types/entities/city"
import { Cinema } from "../types/entities/cinema"
import { appStore } from "../store/appStore"

export const DEFAULT_CINEMA_SLUG = "mega"

export type CinemaSlugMeta = {
    slug: string
    cinemaId: number
    cityName: string
    cinemaName: string
    seoName: string
}

/** Мегасити (id 11) отключён — в карте нет. */
export const CINEMA_SLUG_LIST: CinemaSlugMeta[] = [
    { slug: "mega", cinemaId: 4, cityName: "Челябинск", cinemaName: "Мегаполис", seoName: "Мягкий кинотеатр Мегаполис" },
    { slug: "almaz", cinemaId: 1, cityName: "Челябинск", cinemaName: "Алмаз", seoName: "Мягкий кинотеатр Алмаз" },
    { slug: "fokus", cinemaId: 8, cityName: "Челябинск", cinemaName: "Фокус", seoName: "Мягкий кинотеатр Фокус" },
    { slug: "ufa", cinemaId: 6, cityName: "Уфа", cinemaName: "Мегаполис", seoName: "Мягкий кинотеатр Мегаполис Уфа" },
    { slug: "smr", cinemaId: 2, cityName: "Самара", cinemaName: "Гудок", seoName: "Мягкий кинотеатр Гудок" },
    { slug: "kosmoport", cinemaId: 10, cityName: "Самара", cinemaName: "Космопорт", seoName: "Мягкий кинотеатр Космопорт" },
    { slug: "veer", cinemaId: 12, cityName: "Екатеринбург", cinemaName: "Веер", seoName: "Мягкий кинотеатр Веер" },
    { slug: "otrada", cinemaId: 9, cityName: "Москва", cinemaName: "Отрада", seoName: "Мягкий кинотеатр Отрада" },
    { slug: "depo", cinemaId: 3, cityName: "Нижний Тагил", cinemaName: "Депо", seoName: "Мягкий кинотеатр Депо" },
    { slug: "mgn", cinemaId: 7, cityName: "Магнитогорск", cinemaName: "Семейный парк", seoName: "Мягкий кинотеатр Семейный парк" },
    { slug: "prm", cinemaId: 13, cityName: "Пермь", cinemaName: "СпешиLove", seoName: "Мягкий кинотеатр СпешиLove" },
]

export const CINEMA_BY_SLUG: Record<string, CinemaSlugMeta> = Object.fromEntries(
    CINEMA_SLUG_LIST.map(item => [item.slug, item]),
)

const SLUG_BY_CINEMA_ID: Record<number, string> = Object.fromEntries(
    CINEMA_SLUG_LIST.map(item => [item.cinemaId, item.slug]),
)

export function isCinemaSlug(value: string | undefined): value is string {
    return Boolean(value && CINEMA_BY_SLUG[value])
}

export function getCinemaSlugMeta(slug: string | undefined): CinemaSlugMeta | undefined {
    if (!slug) return undefined
    return CINEMA_BY_SLUG[slug]
}

export function slugForCinemaId(cinemaId: number | undefined): string | undefined {
    if (cinemaId == null) return undefined
    return SLUG_BY_CINEMA_ID[cinemaId]
}

export function parseCinemaPath(pathname: string): { slug: string | null; rest: string } {
    const parts = pathname.split("/").filter(Boolean)
    if (parts[0] && isCinemaSlug(parts[0])) {
        const rest = parts.slice(1).join("/")
        return { slug: parts[0], rest: rest ? `/${rest}` : "/" }
    }
    return { slug: null, rest: pathname || "/" }
}

export function stripCinemaSlug(pathname: string): string {
    return parseCinemaPath(pathname).rest
}

export function isFilmPath(pathname: string): boolean {
    return /\/afisha\/film\/\d+$/.test(stripCinemaSlug(pathname))
}

export function isPromoPath(pathname: string): boolean {
    return /^\/promo\/[^/]+$/.test(stripCinemaSlug(pathname))
}

export function isAfishaPath(pathname: string): boolean {
    const rest = stripCinemaSlug(pathname)
    return rest === "/afisha" || rest.startsWith("/afisha/")
}

export function cinemaHref(slug: string, path: string): string {
    const [pathname, search] = path.split("?")
    const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`
    const url = `/${slug}${normalized === "/" ? "" : normalized}`
    return search ? `${url}?${search}` : url
}

export function storeCinemaSlug(): string {
    const { cinema, locationSelected } = appStore.getState()
    return (locationSelected && slugForCinemaId(cinema.id)) || DEFAULT_CINEMA_SLUG
}

export function hrefForCurrentCinema(path: string): string {
    return cinemaHref(storeCinemaSlug(), path)
}

type RequestResult<T> = { status: "success"; data: T } | { status: "error"; error: { message: string } }

type RequestData = <T>(opts: {
    type: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
    path: string
    disableError?: boolean
}) => Promise<RequestResult<T>>

type LocationLists = { cities: City[]; cinemas: Cinema[] }

let listsPromise: Promise<LocationLists> | null = null

export function loadCinemaLists(requestData: RequestData): Promise<LocationLists> {
    if (!listsPromise) {
        listsPromise = Promise.all([
            requestData<City[]>({ type: "GET", path: "city", disableError: true }),
            requestData<Cinema[]>({ type: "GET", path: "cinema", disableError: true }),
        ]).then(([cityRes, cinemaRes]) => {
            if (cityRes.status !== "success" || cinemaRes.status !== "success") {
                listsPromise = null
                throw new Error("Не удалось загрузить список кинотеатров")
            }
            return {
                cities: cityRes.data ?? [],
                cinemas: cinemaRes.data ?? [],
            }
        })
    }
    return listsPromise
}

export function resolveSlugLocation(
    slug: string,
    lists: LocationLists,
): { city: City; cinema: Cinema; meta: CinemaSlugMeta } | null {
    const meta = getCinemaSlugMeta(slug)
    if (!meta) return null
    const cinema = lists.cinemas.find(item => item.id === meta.cinemaId)
    if (!cinema) return null
    const city = lists.cities.find(item => item.id === cinema.city_id)
    if (!city) return null
    return { city, cinema, meta }
}
